import { HStack, Text, VStack } from '@chakra-ui/react';
import { Panel } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { i18n } from '@investigator/shared/localization';
import { AuthContext, UserPrivileges } from '@investigator/shared/providers';
import { useCallback, useContext, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import { OutletContextType } from './Integrations';
import {
  AvailabilityTag,
  IntegrationsConfigInfo,
} from './IntegrationsConfigCard';
import { IntegrationsConstants } from './IntegrationsConstants';
import { useIdentityIntegrationSettings } from '../hooks/useIdentityIntegrationSettings';
const { integrations } = i18n.pages;
const {
  additionalIntegrations,
  exploreIntegrations,
  fleetIntegrationsHeader,
  fleetIntegrationsTagline,
  investigatorIntegrationsHeader,
  investigatorIntegrationsTagline,
  corelightUpdateIntegrationsHeader,
  corelightUpdateIntegrationsTagline,
  partnerIntegrationsHeader,
  partnerIntegrationsTagline,
} = integrations;

export interface AppConfigProps {
  getAppConfigurationsLoading: boolean;
  tenantPrivileges: UserPrivileges;
}

const enabledIntegrations = Object.keys(IntegrationsConstants).filter(
  (integration) => IntegrationsConstants[integration].available,
);

const investIntegrations = enabledIntegrations.filter(
  (integration) => IntegrationsConstants[integration].type === 'INVEST',
);

const partnerIntegrations = enabledIntegrations.filter(
  (integration) => IntegrationsConstants[integration].type === 'PARTNER',
);
const fleetIntegrations = enabledIntegrations.filter(
  (integration) => IntegrationsConstants[integration].type === 'FLEET',
);
const corelightIntegrations = enabledIntegrations.filter(
  (integration) => IntegrationsConstants[integration].type === 'CORELIGHT',
);

export const IntegrationsConfig = () => {
  const { userLicense, userPrivileges } = useContext(AuthContext);
  const { configsLoading, featureFlags } =
    useOutletContext<OutletContextType>();
  const navigate = useNavigate();
  const { identityIntegrationEnabled } = useIdentityIntegrationSettings();
  const tenant = userLicense?.tenant ?? '';
  const isOrg = userLicense?.federation_type === 'ORG';

  // Safe member list (can contain nulls)
  const members = useMemo(
    () =>
      (userLicense?.members ?? []).filter(
        (m): m is { tenant_id?: string | null } => m != null,
      ),
    [userLicense?.members],
  );

  // Helper: does this privilege evaluate to enabled?
  // - For MEMBER/PRIME: read from current tenant.
  // - For ORG: enabled if ANY member has it.
  const hasPrivilege = useCallback(
    (privName: string): boolean => {
      if (!isOrg) {
        return Boolean(userPrivileges?.[tenant]?.[privName]);
      }
      for (const m of members) {
        const tid = m?.tenant_id ?? undefined;
        if (tid && userPrivileges?.[tid]?.[privName]) return true;
      }
      return false;
    },
    [isOrg, members, tenant, userPrivileges],
  );

  return configsLoading ? (
    <GraphQLReqStatus loading={configsLoading}></GraphQLReqStatus>
  ) : (
    <VStack spacing={4} alignItems="start" maxWidth="960px" marginX="auto">
      <Panel>
        <HStack
          h="200px"
          backgroundSize="535px"
          backgroundPosition="right"
          backgroundRepeat="no-repeat"
          backgroundImage="/assets/images/ig-evidence-based-security.png"
        >
          <VStack ml={6} maxW="550px" alignItems="start">
            <Text textStyle="h4">{additionalIntegrations}</Text>
            <Text mb={2} textStyle="body-md">
              {exploreIntegrations}
            </Text>
          </VStack>
        </HStack>
      </Panel>

      {investIntegrations.length > 0 && (
        <>
          <Text textStyle="h5">{investigatorIntegrationsHeader}</Text>
          <Text>{investigatorIntegrationsTagline}</Text>
          <HStack spacing={4} flexWrap="wrap" justify="flex-start">
            {investIntegrations.map((integration) => {
              const {
                description,
                title,
                pathName,
                tags,
                featureFlag,
                privilegeName,
                fallbackText,
              } = IntegrationsConstants[integration];
              let showTile = true;
              if (featureFlag && featureFlags) {
                showTile = Boolean(featureFlags[featureFlag]);
              }
              if (
                integration === 'microsoft-entra-id' &&
                !identityIntegrationEnabled
              ) {
                showTile = false;
              }
              const enabled = hasPrivilege(privilegeName);
              return showTile ? (
                <IntegrationsConfigInfo
                  availabilityTag={
                    <AvailabilityTag
                      isEnabled={enabled}
                      fallbackText={fallbackText}
                    />
                  }
                  cardView
                  description={description}
                  title={title}
                  onClick={() => {
                    navigate(`../${integration}/configuration`);
                  }}
                  pathName={pathName}
                  tags={tags}
                  isLoading={configsLoading}
                  key={pathName}
                />
              ) : null;
            })}
          </HStack>
        </>
      )}
      {fleetIntegrations.length > 0 && (
        <>
          <Text textStyle="h5">{fleetIntegrationsHeader}</Text>
          <Text>{fleetIntegrationsTagline}</Text>
          <HStack spacing={4} flexWrap="wrap" justify="flex-start">
            {fleetIntegrations.map((integration) => {
              const {
                description,
                title,
                pathName,
                tags,
                featureFlag,
                privilegeName,
                fallbackText,
              } = IntegrationsConstants[integration];
              let showTile = true;
              if (featureFlag && featureFlags) {
                showTile = Boolean(featureFlags[featureFlag]);
              }
              const enabled = hasPrivilege(privilegeName);
              return showTile ? (
                <IntegrationsConfigInfo
                  availabilityTag={
                    <AvailabilityTag
                      isEnabled={enabled}
                      fallbackText={fallbackText}
                    />
                  }
                  cardView
                  description={description}
                  title={title}
                  onClick={() => {
                    navigate(`../${integration}/configuration`);
                  }}
                  pathName={pathName}
                  tags={tags}
                  isLoading={configsLoading}
                  key={pathName}
                />
              ) : null;
            })}
          </HStack>
        </>
      )}
      {corelightIntegrations.length > 0 && (
        <>
          <Text textStyle="h5">{corelightUpdateIntegrationsHeader}</Text>
          <Text>{corelightUpdateIntegrationsTagline}</Text>
          <HStack spacing={4} flexWrap="wrap" justify="flex-start">
            {corelightIntegrations.map((integration) => {
              const {
                fallbackText,
                description,
                title,
                pathName,
                tags,
                featureFlag,
                privilegeName,
              } = IntegrationsConstants[integration];
              let showTile = true;
              if (featureFlag && featureFlags) {
                showTile = Boolean(featureFlags[featureFlag]);
              }
              const enabled = hasPrivilege(privilegeName);
              return showTile ? (
                <IntegrationsConfigInfo
                  availabilityTag={
                    <AvailabilityTag
                      isEnabled={enabled}
                      fallbackText={fallbackText}
                    />
                  }
                  cardView
                  description={description}
                  title={title}
                  onClick={() => {
                    navigate(`../${integration}/configuration`);
                  }}
                  pathName={pathName}
                  tags={tags}
                  isLoading={configsLoading}
                  key={pathName}
                />
              ) : null;
            })}
          </HStack>
        </>
      )}
      {partnerIntegrations.length > 0 && (
        <>
          <Text textStyle="h5">{partnerIntegrationsHeader}</Text>
          <Text>{partnerIntegrationsTagline}</Text>
          <HStack spacing={4} flexWrap="wrap" justify="flex-start">
            {partnerIntegrations.map((integration) => {
              const {
                description,
                title,
                pathName,
                tags,
                featureFlag,
                privilegeName,
                fallbackText,
              } = IntegrationsConstants[integration];
              let showTile = true;
              if (featureFlag && featureFlags) {
                showTile = Boolean(featureFlags[featureFlag]);
              }
              const enabled = hasPrivilege(privilegeName);
              return showTile ? (
                <IntegrationsConfigInfo
                  availabilityTag={
                    <AvailabilityTag
                      isEnabled={enabled}
                      fallbackText={fallbackText}
                    />
                  }
                  cardView
                  description={description}
                  title={title}
                  onClick={() => {
                    navigate(`../${integration}/configuration`);
                  }}
                  pathName={pathName}
                  tags={tags}
                  key={pathName}
                  isLoading={configsLoading}
                />
              ) : null;
            })}
          </HStack>
        </>
      )}
    </VStack>
  );
};
